import { cn } from '@plato/ui/util/classes';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

const textVariants = cva('', {
    variants: {
        variant: {
            h1: [],
            ai: 'bg-linear-to-r from-ai-base to-ai-highlight bg-clip-text',
        },
        size: {
            xs: 'text-xs',
            xs2: 'text-xs',
            sm: 'text-sm',
            base: 'text-base',
            lg: 'text-lg',
            xl: 'text-xl',
        },
        color: {
            inherit: 'text-inherit',
            'neutral-300': 'text-neutral-300',
            'neutral-500': 'text-neutral-500',
            'neutral-600': 'text-neutral-600',
            'neutral-700': 'text-neutral-700',
            'neutral-800': 'text-neutral-800',
            'neutral-900': 'text-neutral-900',
            'neutral-950': 'text-neutral-950',
            'blue-600': 'text-blue-600',
            'red-600': 'text-red-600',
            'orange-600': 'text-orange-600',
        },
        weight: {
            normal: 'font-normal',
            bold: 'font-bold',
            medium: 'font-medium',
            semibold: 'font-semibold',
        },
        leading: {
            normal: 'leading-normal',
            none: 'leading-none',
            tight: 'leading-tight',
            snug: 'leading-snug',
        },
    },
    defaultVariants: {
        size: 'sm',
        color: 'neutral-700',
        weight: 'normal',
    },
    compoundVariants: [
        {
            variant: 'h1',
            color: 'neutral-700',
            className: ['text-neutral-950 font-medium'],
        },
        {
            variant: 'ai',
            color: 'neutral-700',
            className: 'text-transparent',
        },
    ],
});

export type CommonTextProps = {
    asChild?: boolean;
    className?: string;
} & React.PropsWithChildren &
    VariantProps<typeof textVariants>;

type TextSpanProps = { as?: 'span' } & React.ComponentPropsWithoutRef<'span'>;
type TextDivProps = { as: 'div' } & React.ComponentPropsWithoutRef<'div'>;
type TextLabelProps = { as: 'label' } & React.ComponentPropsWithoutRef<'label'>;
type TextPProps = { as: 'p' } & React.ComponentPropsWithoutRef<'p'>;

export type TextProps = CommonTextProps & (TextSpanProps | TextDivProps | TextLabelProps | TextPProps);

export const Text = React.forwardRef<React.ElementRef<'span'>, TextProps>(
    (
        {
            // variants
            variant,
            size,
            color,
            weight,
            leading,
            // other props
            children,
            asChild,
            as: Tag = 'span',
            className,
            ...props
        },
        forwardedRef,
    ) => {
        return (
            <Slot
                {...props}
                ref={forwardedRef}
                className={cn(
                    textVariants({
                        size,
                        color,
                        weight,
                        leading,
                        variant,
                        className,
                    }),
                )}
            >
                {asChild ? children : <Tag>{children}</Tag>}
            </Slot>
        );
    },
);

Text.displayName = 'Text';
export default Text;
